import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Kbd } from './Button';
import { IconSearch } from './icons';
import { useKit } from './strings';

export type Command = {
  id: string;
  label: string;
  group?: string;
  /** Extra search terms (synonyms, the other language's word, the route). */
  keywords?: string[];
  hint?: string;
  icon?: ReactNode;
  disabled?: boolean;
  run: () => void;
};

/** Lower-case, strip Arabic diacritics and tatweel, fold alef/yeh/teh-marbuta variants, collapse spaces. */
export function normalize(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ًͯ-ٰٟـ]/g, '')
    .replace(/[آأإ]/g, 'ا')
    .replace(/ى/g, 'ي')
    .replace(/ة/g, 'ه')
    .replace(/\s+/g, ' ')
    .trim();
}

/** 0 = no match. Higher is better: exact > prefix > word prefix > substring > in-order letters. */
export function scoreText(text: string, query: string): number {
  const t = normalize(text);
  const q = normalize(query);
  if (!q) return 1;
  if (t === q) return 100;
  if (t.startsWith(q)) return 80;
  if (t.split(' ').some((w) => w.startsWith(q))) return 60;
  if (t.includes(q)) return 40;
  let i = 0;
  for (const ch of t) if (ch === q[i]) i++;
  return i === q.length ? 10 : 0;
}

/** Commands matching every query word, best first; label matches outrank keyword matches. Order is stable. */
export function filterCommands(commands: Command[], query: string): Command[] {
  const words = normalize(query).split(' ').filter(Boolean);
  if (!words.length) return commands;
  return commands
    .map((c, i) => {
      let total = 0;
      for (const w of words) {
        // labels may match loosely (letters in order); keywords and group names only as whole substrings
        const strict = (t: string) => { const v = scoreText(t, w); return v >= 40 ? v : 0; };
        const s = Math.max(scoreText(c.label, w), ...(c.keywords ?? []).map((k) => strict(k) * 0.8), c.group ? strict(c.group) * 0.5 : 0);
        if (!s) return null;
        total += s;
      }
      return { c, i, total };
    })
    .filter((x): x is { c: Command; i: number; total: number } => x !== null)
    .sort((a, b) => b.total - a.total || a.i - b.i)
    .map((x) => x.c);
}

/** True for Cmd+K (macOS) or Ctrl+K (elsewhere). */
export const isPaletteHotkey = (e: { key: string; metaKey: boolean; ctrlKey: boolean; altKey: boolean; shiftKey?: boolean }) =>
  e.key.toLowerCase() === 'k' && (e.metaKey || e.ctrlKey) && !e.altKey;

/** Global Cmd/Ctrl+K listener. Toggles; works even while focus is in a text field. */
export function usePaletteHotkey(onToggle: () => void) {
  useEffect(() => {
    const h = (e: globalThis.KeyboardEvent) => { if (isPaletteHotkey(e)) { e.preventDefault(); onToggle(); } };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onToggle]);
}

/**
 * Command palette (combobox + listbox in a modal dialog). Type to filter pages and actions, arrows to move,
 * Enter to run, Esc to close. Results keep their groups in first-seen order.
 */
export function CommandPalette({ open, onClose, commands, placeholder }: { open: boolean; onClose: () => void; commands: Command[]; placeholder?: string }) {
  const { strings } = useKit();
  const ref = useRef<HTMLDialogElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const base = useId().replace(/:/g, '');
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const results = useMemo(() => filterCommands(commands, query), [commands, query]);
  const enabled = results.filter((c) => !c.disabled);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) { el.showModal?.(); setQuery(''); setActive(0); input.current?.focus(); }
    else if (!open && el.open) el.close();
  }, [open]);
  useEffect(() => setActive(0), [query]);
  useEffect(() => {
    list.current?.querySelector('[aria-selected="true"]')?.scrollIntoView?.({ block: 'nearest' });
  }, [active]);

  const run = (c: Command | undefined) => { if (!c || c.disabled) return; onClose(); c.run(); };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!enabled.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => (a + 1) % enabled.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => (a - 1 + enabled.length) % enabled.length); }
    else if (e.key === 'Home') { e.preventDefault(); setActive(0); }
    else if (e.key === 'End') { e.preventDefault(); setActive(enabled.length - 1); }
    else if (e.key === 'Enter') { e.preventDefault(); run(enabled[active]); }
  };
  const activeCmd = enabled[active];
  const groups: Array<[string, Command[]]> = [];
  for (const c of results) {
    const g = c.group ?? '';
    const found = groups.find(([k]) => k === g);
    if (found) found[1].push(c); else groups.push([g, [c]]);
  }
  const optId = (c: Command) => `${base}-o-${c.id}`;

  return (
    <dialog
      ref={ref}
      className="qk-dialog qk-cmd qk"
      aria-label={strings.commandLabel}
      onCancel={(e) => { e.preventDefault(); onClose(); }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="qk-cmd__search">
        <IconSearch />
        <input
          ref={input}
          className="qk-cmd__input"
          type="text"
          role="combobox"
          aria-expanded="true"
          aria-controls={`${base}-list`}
          aria-autocomplete="list"
          aria-activedescendant={activeCmd ? optId(activeCmd) : undefined}
          aria-label={strings.commandLabel}
          placeholder={placeholder ?? strings.commandPlaceholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKey}
          autoComplete="off"
          spellCheck={false}
        />
        <Kbd>Esc</Kbd>
      </div>
      <ul className="qk-cmd__list" id={`${base}-list`} role="listbox" ref={list} aria-label={strings.commandLabel}>
        {groups.map(([g, cmds]) => (
          <li key={g || '_'} role="presentation">
            {g ? <div className="qk-cmd__group" role="presentation" id={`${base}-g-${g}`}>{g}</div> : null}
            <ul role="group" aria-labelledby={g ? `${base}-g-${g}` : undefined} className="qk-cmd__group-list">
              {cmds.map((c) => {
                const on = c === activeCmd;
                return (
                  <li
                    key={c.id}
                    id={optId(c)}
                    role="option"
                    aria-selected={on}
                    aria-disabled={c.disabled || undefined}
                    className="qk-cmd__item"
                    onMouseMove={() => { const i = enabled.indexOf(c); if (i >= 0 && i !== active) setActive(i); }}
                    onClick={() => run(c)}
                  >
                    {c.icon ? <span className="qk-cmd__item-icon" aria-hidden="true">{c.icon}</span> : null}
                    <span className="qk-cmd__item-label">{c.label}</span>
                    {c.hint ? <span className="qk-cmd__item-hint">{c.hint}</span> : null}
                  </li>
                );
              })}
            </ul>
          </li>
        ))}
      </ul>
      {results.length === 0 ? <p className="qk-cmd__empty" role="status">{strings.commandEmpty}</p> : null}
      <div className="qk-cmd__footer" aria-hidden="true">
        <span><Kbd>↑</Kbd><Kbd>↓</Kbd> {strings.commandNavigate}</span>
        <span><Kbd>↵</Kbd> {strings.commandSelect}</span>
        <span><Kbd>Esc</Kbd> {strings.commandClose}</span>
      </div>
    </dialog>
  );
}
